/**
 * Header + tab-count helpers of the claim detail page (fiches 22807 /
 * 22746): the page title / subtitle / breadcrumb label / status badge from
 * the `WS2_EA_FIC_SIN` record, falling back to the list row carried in the
 * router state (deep links have none), and the « Pièces » count.
 */

import type { BadgeVariant } from '@components/base/display';

import type { ClaimRecord } from '../../api/claimsService';
import type { PieceItem } from '../shared/pieceItems';
import { normaliseStatus, resolveStatusBadge } from '../shared/statusBadgeConfig';
import type { ClaimRouteState } from './claimRouteState';
import { CLAIM_STATUS_BADGE } from './claimsText';

/** Raw piece status counted by the « Pièces » tab. */
export const PENDING_PIECE_STATUS = 'EN ATTENTE';

export interface ClaimDetailHeader {
  /** `NOM_ASSURE`, else the list's `ASSURE`, else the generic title. */
  title: string;
  /** `LIB_PROD · NCBT_SIN` (missing parts skipped); `undefined` when both are missing. */
  subtitle: string | undefined;
  /** Breadcrumb label of the claim: `NCBT_SIN`, else the route id. */
  crumb: string;
  /** Status badge (`LIB_MOT`, coloured by `ETAT`); `null` when there is no label. */
  status: { label: string; variant: BadgeVariant } | null;
}

/** Build the detail header from the record (loaded or not) and the router state. */
export function buildClaimDetailHeader(
  record: ClaimRecord | null | undefined,
  routeState: ClaimRouteState,
  idSin: string,
  fallbackTitle: string,
): ClaimDetailHeader {
  const reference = record?.reference ?? routeState.reference;
  const produit = record?.produit ?? routeState.produit;
  const subtitleParts = [produit, reference].filter(
    (part): part is string => part !== null && part !== '',
  );
  const statusLabel = record?.libelleStatut ?? null;

  return {
    title: record?.nomAssure ?? routeState.assure ?? fallbackTitle,
    subtitle: subtitleParts.length > 0 ? subtitleParts.join(' · ') : undefined,
    crumb: reference ?? idSin,
    status:
      statusLabel === null
        ? null
        : {
            label: statusLabel,
            variant: resolveStatusBadge(record?.etat, CLAIM_STATUS_BADGE)?.variant ?? 'default',
          },
  };
}

/** Number of pieces still awaited (`STATUT` « EN ATTENTE »). */
export function countPendingPieces(pieces: readonly PieceItem[]): number {
  return pieces.filter(
    (piece) => piece.statut !== null && normaliseStatus(piece.statut) === PENDING_PIECE_STATUS,
  ).length;
}
